import {
  DetailedDriveStatistics,
  DriveSubinterval,
  ScheduledDriveResponse,
  RequirementSubintervalsResponse,
  BillingPeriodResponse,
} from '@/api/main/types.gen'
import { groupBy, partition } from 'lodash-es'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { dateSpanToArray, getTimezone } from './datetime'
import { getTimelineStart } from '@/components/Scheduling/schedulingTimelineHelpers'

dayjs.extend(utc)
dayjs.extend(timezone)

export type ScheduledDriveInterval = DriveSubinterval

type PartitionedIntervals = {
  past: DriveSubinterval[]
  rest: DriveSubinterval[]
}

export const partitionIntervalsByTime = (intervals: DriveSubinterval[], currentTime?: Date): PartitionedIntervals => {
  const now = currentTime || new Date()
  const timezone = getTimezone()

  let [past, rest] = partition(intervals, interval => {
    const intervalEnd = dayjs.utc(interval.interval.ending_at).tz(timezone)
    const tzNow = dayjs(now).tz(timezone)
    return intervalEnd.isBefore(tzNow)
  })

  rest = rest.map(interval => ({
    ...interval,
    drives: interval.drives.filter(
      drive =>
        drive.drive_state !== 'finished' &&
        drive.drive_state !== 'cancelled' &&
        drive.drive_state !== 'cancelled_in_progress'
    ),
  }))

  return { past, rest }
}

export type DrivesByStateByVehicle = {
  [vehicleId: string]: {
    finished: DetailedDriveStatistics[]
    rest: DetailedDriveStatistics[]
  }
}

export const paritionDrivesByState = (detailedDriveStatistics: DetailedDriveStatistics[]): DrivesByStateByVehicle => {
  const groupedByVehicle = groupBy(detailedDriveStatistics, 'vehicle_id')

  return Object.fromEntries(
    Object.entries(groupedByVehicle).map(([vehicleId, drives]) => {
      const [finished, rest] = partition(drives, drive => drive.state === 'finished')
      return [vehicleId, { finished, rest }]
    })
  )
}

export const estimateDriveTimeByRouteId = ({ estimated_duration }: ScheduledDriveResponse) => estimated_duration ?? 3600

export const splitDrivesByDay = (
  driveStatistics: DetailedDriveStatistics[],
  intervalStart: string,
  intervalEnd: string
): Array<{ drives: DrivesByStateByVehicle; timelineStart: dayjs.Dayjs }> => {
  const timezone = getTimezone()
  const startDate = dayjs.utc(intervalStart).tz(timezone)
  const endDate = dayjs.utc(intervalEnd).tz(timezone)
  const dates = dateSpanToArray(startDate, endDate)

  // Group finished drives by day
  const finishedDrives = driveStatistics.filter(
    drive => drive.state === 'finished' || drive.state === 'cancelled_in_progress'
  )

  return dates
    .map(dateStr => {
      // Parse date string correctly (format: YYYY-MM-DD)
      const date = dayjs(dateStr).tz(timezone)
      const timelineStart = getTimelineStart(date)
      const nextDayStart = getTimelineStart(date.add(1, 'day'))

      // Filter drives that overlap with this day's timeline (from timelineStart to nextDayStart)
      // A drive overlaps if: driveStart < nextDayStart && driveEnd > timelineStart
      const dayDrives = finishedDrives.filter(drive => {
        // Parse timestamps as UTC (API returns UTC) then convert to configured timezone
        const driveStart = dayjs.utc(drive.drive_started_at).tz(timezone)
        const driveEnd = dayjs.utc(drive.drive_finished_at).tz(timezone)

        // Check if drive overlaps with the timeline period
        return driveStart.isBefore(nextDayStart) && driveEnd.isAfter(timelineStart)
      })

      // Group by vehicle
      const drivesByVehicle: DrivesByStateByVehicle = {}
      dayDrives.forEach(drive => {
        if (!drivesByVehicle[drive.vehicle_id]) {
          drivesByVehicle[drive.vehicle_id] = { finished: [], rest: [] }
        }
        drivesByVehicle[drive.vehicle_id].finished.push(drive)
      })

      return {
        drives: drivesByVehicle,
        timelineStart,
      }
    })
    .filter(dayData => {
      // Filter out days with no drives
      const hasDrives = Object.values(dayData.drives).some(vehicleDrives => vehicleDrives.finished.length > 0)
      return hasDrives
    })
}

/**
 * Get billing periods that overlap with a given date range
 */
export const getOverlappingBillingPeriods = (
  startDate: string,
  endDate: string,
  billingPeriods: BillingPeriodResponse[]
): BillingPeriodResponse[] => {
  const timezone = getTimezone()
  const start = dayjs.utc(startDate).tz(timezone)
  const end = dayjs.utc(endDate).tz(timezone)

  return billingPeriods.filter(period => {
    const periodStart = dayjs.utc(period.dates.starting_from).tz(timezone).startOf('day')
    const periodEnd = dayjs.utc(period.dates.ending_at).tz(timezone).endOf('day')
    // A period overlaps if: periodStart <= end && periodEnd >= start
    return periodStart.isBefore(end) && periodEnd.isAfter(start)
  })
}

/**
 * Find which billing period contains a specific time point
 * Since we're working within default bounds, we just need to check which period contains the time
 */
export const findBillingPeriodForTime = (time: string, overlappingPeriods: BillingPeriodResponse[]): number | null => {
  const timezone = getTimezone()
  const timePoint = dayjs.utc(time).tz(timezone)

  for (const period of overlappingPeriods) {
    const periodStart = dayjs.utc(period.dates.starting_from).tz(timezone).startOf('day')
    const periodEnd = dayjs.utc(period.dates.ending_at).tz(timezone).endOf('day')

    // Check if time point is within this billing period (inclusive)
    if (!timePoint.isBefore(periodStart) && !timePoint.isAfter(periodEnd)) {
      return period.id
    }
  }

  // If not found, return the first overlapping period as fallback
  return overlappingPeriods.length > 0 ? overlappingPeriods[0].id : null
}

/**
 * Aggregate multiple RequirementSubintervalsResponse objects into a single one
 * Merges drive_subintervals, requirement_subintervals, and requirements
 */
export const aggregateRequirementSubintervals = (
  results: RequirementSubintervalsResponse[]
): RequirementSubintervalsResponse => {
  if (results.length === 0) {
    return {
      requirements: {},
      drive_subintervals: [],
      requirement_subintervals: [],
    }
  }

  if (results.length === 1) {
    return results[0]
  }

  // Merge drive_subintervals (concatenate arrays and deduplicate by vehicle_id + interval)
  const drive_subintervalsMap = new Map<string, DriveSubinterval>()
  results.forEach(result => {
    result.drive_subintervals.forEach(interval => {
      // Create a unique key from vehicle_id and interval times
      const key = `${interval.vehicle_id}-${interval.interval.starting_from}-${interval.interval.ending_at}`
      // If we already have this interval, merge the drives (avoid duplicates)
      if (drive_subintervalsMap.has(key)) {
        const existing = drive_subintervalsMap.get(key)!
        // Merge drives, avoiding duplicates by drive id
        const existingDriveIds = new Set(existing.drives.map((d: { id: number }) => d.id))
        const newDrives = interval.drives.filter((d: { id: number }) => !existingDriveIds.has(d.id))
        existing.drives.push(...newDrives)
      } else {
        drive_subintervalsMap.set(key, { ...interval })
      }
    })
  })
  const drive_subintervals = Array.from(drive_subintervalsMap.values())

  // Merge requirement_subintervals (concatenate arrays)
  const requirement_subintervals = results.flatMap(result => result.requirement_subintervals)

  // Merge requirements objects (combine all keys, later values override earlier ones for same key)
  const requirements = results.reduce(
    (acc, result) => {
      return { ...acc, ...result.requirements }
    },
    {} as RequirementSubintervalsResponse['requirements']
  )

  return {
    requirements,
    drive_subintervals,
    requirement_subintervals,
  }
}
